import React from 'react';
import styled from 'styled-components';

/**
 * Card — CHANGED in 1.17.0-dev.1.
 *
 * The card is white with a 1px rgba(0,0,0,.1) hairline, 6px corners and the
 * faintest app shadow — the client's approved geometry ("blød"). The website
 * itself has no card elevation at all and is square at 0, so this is the one
 * place the app is deliberately softer than the site.
 *
 * The `elevation` prop is kept so no call site breaks, but it now only steps up
 * from that baseline:
 *   none | sm | md -> the default lift (--shadow-xs)   lg -> --shadow-sm
 * Use lg for surfaces that genuinely float (a popover, a dragged row), not for
 * ordinary content cards.
 *
 * Titles are uppercase bold, as every heading in this brand is.
 */
const CardContainer = styled.div`
  background-color:${({ theme }) => theme.components.card.backgroundColor};
  border:${({ theme }) => theme.components.card.border};
  border-radius:${({ theme }) => theme.components.card.borderRadius};
  box-shadow:${({ theme, elevation }) =>
    elevation === 'lg' ? theme.shadows.sm : theme.components.card.boxShadow};
  overflow:hidden;
  display:flex;
  flex-direction:column;
  height:${({ fullHeight }) => (fullHeight ? '100%' : 'auto')};
`;

const CardHeader = styled.div`
  padding:${({ theme }) => `${theme.spacing[4]} ${theme.spacing[6]}`};
  border-bottom:${({ theme, divider }) => (divider ? `1px solid ${theme.colors.hairline}` : 'none')};
  display:flex;
  justify-content:${({ align }) => align || 'space-between'};
  align-items:center;
  gap:${({ theme }) => theme.spacing[4]};
`;

const CardTitle = styled.h3`
  margin:0;
  font-size:${({ theme, size }) =>
    size === 'sm' ? theme.typography.fontSize.base :
    size === 'lg' ? theme.typography.fontSize.xl :
    theme.typography.fontSize.md};
  font-weight:${({ theme }) => theme.typography.fontWeight.bold};
  text-transform:uppercase;
  letter-spacing:${({ theme }) => theme.typography.letterSpacing.normal};
  line-height:${({ theme }) => theme.typography.lineHeight.tight};
  color:${({ theme }) => theme.colors.dark};
`;

/* Subtitles stay sentence case and light — this is where the voice softens. */
const CardSubtitle = styled.div`
  color:${({ theme }) => theme.colors.textSecondary};
  font-size:${({ theme }) => theme.typography.fontSize.xs};
  font-weight:${({ theme }) => theme.typography.fontWeight.light};
  margin-top:${({ theme }) => theme.spacing[1]};
`;

const CardContent = styled.div`
  padding:${({ theme, padding }) =>
    padding === 'none' ? '0' :
    padding === 'sm' ? `${theme.spacing[4]} ${theme.spacing[5]}` :
    padding === 'lg' ? `${theme.spacing[6]} ${theme.spacing[8]}` :
    `${theme.spacing[5]} ${theme.spacing[6]}`};
  flex:1;
`;

const CardFooter = styled.div`
  padding:${({ theme }) => `${theme.spacing[4]} ${theme.spacing[6]}`};
  border-top:${({ theme, divider }) => (divider ? `1px solid ${theme.colors.hairline}` : 'none')};
  display:flex;
  gap:${({ theme }) => theme.spacing[3]};
  justify-content:${({ align }) =>
    align === 'left' ? 'flex-start' :
    align === 'right' ? 'flex-end' :
    align === 'center' ? 'center' :
    'space-between'};
  align-items:center;
`;

const Card = ({
  title,
  subtitle,
  headerActions,
  footerContent,
  children,
  elevation = 'none',
  fullHeight = false,
  headerDivider = true,
  footerDivider = true,
  padding = 'md',
  footerAlign = 'right',
  titleSize = 'md',
  ...props
}) => {
  return (
    <CardContainer elevation={elevation} fullHeight={fullHeight} {...props}>
      {(title || headerActions) && (
        <CardHeader divider={headerDivider}>
          <div>
            {title && <CardTitle size={titleSize}>{title}</CardTitle>}
            {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
          </div>
          {headerActions && <div>{headerActions}</div>}
        </CardHeader>
      )}

      <CardContent padding={padding}>
        {children}
      </CardContent>

      {footerContent && (
        <CardFooter divider={footerDivider} align={footerAlign}>
          {footerContent}
        </CardFooter>
      )}
    </CardContainer>
  );
};

export default Card;
